// --- Dependencies -------------------------------------------------------------------------------
import { NavLink } from "react-router-dom";
// --- Our Modules --------------------------------------------------------------------------------
import useLang from "@/hooks/useLang";
import useTranslation from "@/hooks/useTranslation";
import type { TyOverviewCoupons, TyOverviewMeta } from "@/types";
import StatTile from "@/UI/atoms/StatTile";
import { formatMoney, formatNumber, rangeLabel } from "./helpers";

// --- Types --------------------------------------------------------------------------------------

type Props = {
    data: TyOverviewCoupons;
    meta: TyOverviewMeta;
};

// --- Logic --------------------------------------------------------------------------------------

/**
 * Headline figures only. Per-code usage is a lookup an admin arrives at already knowing which code they
 * are asking about, so it belongs on the coupons screen itself rather than competing for room here.
 */
export default function CouponsSection({ data, meta }: Props) {
    const t = useTranslation();
    const lang = useLang((state) => state.lang);

    const period = rangeLabel(meta.range, t);

    return (
        <section className="home-section">
            <div className="home-section-head">
                <h2 className="home-section-title">{t.home.couponsTitle}</h2>
                <NavLink className="home-section-link" to="/coupons">
                    {t.home.viewAll}
                </NavLink>
            </div>

            <div className="home-tiles">
                <StatTile label={t.home.couponsTotal} value={formatNumber(data.total, lang)} tone="brand" to="/coupons" />
                <StatTile label={t.home.couponsActive} value={formatNumber(data.active, lang)} tone="good" to="/coupons" />
                <StatTile label={t.home.couponsRedeemed} value={formatNumber(data.redeemed, lang)} hint={period} to="/coupons" />
                <StatTile label={t.home.couponsDiscounted} value={formatMoney(data.discounted, lang, t)} hint={period} tone="warn" to="/coupons" />
            </div>
        </section>
    );
}
